import { memo, useCallback } from 'react';
import { Mic, Send, Square, Voicemail } from 'lucide-react';
import { Button } from '@cyphex/ui';
import { useVoiceRecorder } from '@/features/chat/hooks/useVoiceRecorder';
import { formatDuration } from '../lib/format';

export interface VoicemailComposerProps {
  /** Who the message will reach. Shown so nobody records into the void. */
  recipientName: string;
  isSending: boolean;
  isSent: boolean;
  onSend: (clip: { blob: Blob; durationMs: number; waveform: number[] }) => void;
}

/**
 * "Leave a message" — the recorder behind the call-ended screen.
 *
 * The Voicemail tab, the history badge and the inline player all shipped
 * against a table nothing ever wrote to. This is the missing half: a real
 * microphone capture (the same MediaRecorder hook chat voice messages use)
 * which uploads to object storage and becomes a row the recipient can play.
 */
export const VoicemailComposer = memo(function VoicemailComposer(props: VoicemailComposerProps) {
  const recorder = useVoiceRecorder();

  const stopAndSend = useCallback(() => {
    void recorder.stop().then((clip) => {
      if (clip !== null) props.onSend(clip);
    });
  }, [recorder, props]);

  if (props.isSent) {
    return (
      <p className="flex items-center gap-2 text-base text-stage-muted">
        <Voicemail size={16} aria-hidden />
        Message sent to {props.recipientName}.
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-base text-stage-muted">
        {recorder.isRecording
          ? `Recording — ${formatDuration(recorder.elapsedMs)}`
          : `Leave ${props.recipientName} a voicemail`}
      </p>

      {recorder.isRecording ? (
        <div className="flex items-center gap-3">
          {/* A live meter, not a spinner: the one thing a person recording a
              message needs to know is that the microphone is hearing them. */}
          <span
            aria-hidden
            className="h-2 w-32 overflow-hidden rounded-pill bg-white/15"
          >
            <span
              className="block h-full rounded-pill bg-destructive transition-all duration-fast"
              style={{ width: `${String(Math.min(100, recorder.level))}%` }}
            />
          </span>
          <Button variant="danger" leftIcon={Square} onClick={recorder.cancel}>
            Discard
          </Button>
          <Button
            variant="primary"
            leftIcon={Send}
            onClick={stopAndSend}
            disabled={props.isSending}
          >
            Send
          </Button>
        </div>
      ) : (
        <Button
          variant="secondary"
          leftIcon={Mic}
          onClick={() => {
            void recorder.start();
          }}
          disabled={props.isSending}
        >
          Leave a message
        </Button>
      )}
    </div>
  );
});
